import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router, RouterModule } from '@angular/router';
import { PredictiveSearcherComponent } from '@ildes/basic/predictive-searcher/predictive-searcher.component';
import { ApiManagerService } from '@ildes/services/api-manager.service';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { PRIVACY_VERSION } from '@ildes/views/policies/policies-version';
import { MatCardModule } from '@angular/material/card';

@Component({
    selector: 'app-contact-pqr',
    standalone: true,
    imports: [
        FormsModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        RouterModule,
        MatButtonModule,
        MatInputModule,
        PredictiveSearcherComponent,
        MatCheckboxModule,
        CommonModule,
        MatCardModule
    ],
    template: `
    <mat-card class="content">
        <form [formGroup]="pqr" class="padding-top-8 padding-bottom-8">
        <div>
            <mat-form-field appearance="outline" class="full-width">
            <mat-label>Nombre</mat-label>
            <input matInput formControlName="name">
            <mat-error>
                El nombre es obligatorio
            </mat-error>
            </mat-form-field>
        </div>
        <div>
            <mat-form-field appearance="outline"  class="full-width">
            <mat-label>Apellidos</mat-label>
            <input matInput formControlName="surname">
            <mat-error>
                Los apellidos son obligatorios
            </mat-error>
            </mat-form-field>
        </div>
        <div>
            <mat-form-field appearance="outline"  class="full-width">
            <mat-label>Email</mat-label>
            <input matInput formControlName="email">
            <mat-error>
                El email es obligatorio
            </mat-error>
            </mat-form-field>
        </div>
        <div>
            <mat-form-field appearance="outline"  class="full-width">
            <mat-label>Dirección de la incidencia</mat-label>
            <input matInput formControlName="address">
            <mat-error>
                La dirección es obligatoria
            </mat-error>
            </mat-form-field>
        </div>
        <div class="inline-flex" *ngIf="this.pqr.controls?.department">
            <predictive-searcher
                class="column margin-right-8"
                formControlName="department"
                label="Departamento"
                mandatorySelection="true"
                (selectedValue)="changeDepartment($event)"
                [service]="searchDepartmentAdapter"></predictive-searcher>
            <predictive-searcher
                class="column"
                mandatorySelection="true"
                [disabled]="!this.pqr?.controls?.department?.value?.selected?.name"
                formControlName="city"
                label="Municipio"
                [service]="searchCityAdapter"></predictive-searcher>
        </div>
        <mat-form-field appearance="outline" class="full-width">
            <mat-label>Motivo de la consulta</mat-label>
            <textarea formControlName="requestDescription" placeholder="Indique brevemente el motivo del PQRS" rows="4" maxlength="256" matInput></textarea>
        </mat-form-field>
        <div>
            <div class="padding-bottom-22" *ngIf="!clickInPolicy">
                Lea detenidamente nuestra <a (click)="gotoPrivacyPolicy()">Política de Privacidad</a> antes de continuar.
            </div>
            <mat-checkbox formControlName="termsAndConditions" class="padding-bottom-22" *ngIf="clickInPolicy">
                Autorizo el tratamiento de mis datos personales conforme a la <a (click)="gotoPrivacyPolicy()">Política de Privacidad</a>
            </mat-checkbox>
        </div>

        <div class="align-center">
            <div class="padding-bottom-8">
            <button
                (click)="onSubmitPQR()"
                [disabled]="pqr?.invalid || loading"
                mat-raised-button
                color="primary"
                class="button-size-200">Enviar</button>
            </div>
            <div>
            <!-- <button mat-button>He olvidado la contraseña</button> -->
            <mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>
            </div>
        </div>
        </form>
    <mat-card>
  `,
    styleUrls: ['./contact-pqr.component.css'],
})
export class ContactPqrComponent implements OnInit {
    public searchDepartmentAdapter;
    public searchCityAdapter;
    public currentDepartment;
    public luminaryId;
    pqr;
    clickInPolicy = false;
    loading;

    constructor(
        private apiManager: ApiManagerService,
        private recaptchaV3Service: ReCaptchaV3Service,
        private router: Router,
        private cd: ChangeDetectorRef
    ) {
        this.clickInPolicy = sessionStorage.getItem('clickInPolicy') === 'true';
        this.searchDepartmentAdapter = (data) => {
            return this.apiManager.findDepartment({ text: data.text });
        };
        this.searchCityAdapter = (data) => {
            const classification = this.pqr.controls.department.value.selected.name;

            return this.apiManager.findCity({ text: data.text, classification });
        };
    }

    onSubmitPQR() {
        const privacyPolicyVersion = PRIVACY_VERSION; // TODO: obtener de la fecha de la política real
        const {
            name,
            surname,
            email,
            address,
            city,
            department,
            requestDescription,
            termsAndConditions
        } = this.pqr.controls;
        this.loading = true;
        this.recaptchaV3Service.execute('importantAction')
            .subscribe((token: string) => {
                this.apiManager.postContactPqr({
                    luminaryId: this.luminaryId,
                    name: name.value,
                    surname: surname.value,
                    email: email.value,
                    address: address.value,
                    city: city?.value?.selected?.name,
                    privacyPolicyCheck: termsAndConditions.value,
                    privacyPolicyVersion,
                    department: department?.value?.selected?.name,
                    requestDescription: requestDescription.value
                }, { error: true, timeout: false, token }).subscribe(() => {
                    this.pqr.reset();
                    Object.keys(this.pqr.controls).forEach(key => {
                        this.pqr.get(key)?.setErrors(null);
                        this.pqr.get(key)?.markAsPristine();
                        this.pqr.get(key)?.markAsUntouched();
                    });
                    if (this.luminaryId) {
                        this.luminaryId = null;
                        history.replaceState({}, '', this.router.url);
                    }

                    this.performForm();
                    this.loading = false;
                    Object.keys(this.pqr.controls).forEach(key => {
                        this.pqr.get(key)?.setErrors(null);
                        this.pqr.get(key)?.markAsPristine();
                        this.pqr.get(key)?.markAsUntouched();
                    });
                    sessionStorage.removeItem('pqrData');
                }, () => {
                    this.loading = false;
                });

            });
    }

    performForm() {
        if (!this.pqr) {
            this.pqr = new FormGroup({
                name: new FormControl('', Validators.required),
                surname: new FormControl('', Validators.required),
                email: new FormControl('', [Validators.email, Validators.required]),
                address: new UntypedFormControl('', Validators.required),
                requestDescription: new FormControl('', Validators.required),
                termsAndConditions: new FormControl(false, Validators.requiredTrue)
            });
        }

        if (!this.pqr.controls.department && !this.luminaryId) {
            this.pqr.addControl('department', new UntypedFormControl('', Validators.required));
            this.pqr.addControl('city', new UntypedFormControl('', Validators.required));
            this.cd.detectChanges()
        }

    }
    ngOnInit() {
        this.luminaryId = history.state.id;
        this.performForm();

        const data = JSON.parse(sessionStorage.getItem('pqrData') || '{}');

        Object.keys(data).forEach((key) => {
            this.pqr.controls[key].setValue(data[key]);
        });
    }
    changeDepartment($event) {
        if (this.currentDepartment && this.currentDepartment !== $event.option.value?.name) {
            this.currentDepartment = $event?.name;
            this.pqr.controls.city.reset();
        }
    }

    gotoPrivacyPolicy() {
        this.clickInPolicy = true;
        const data = {};

        Object.keys(this.pqr.controls).forEach((key) => {
            data[key] = this.pqr.controls[key].value;
        })
        sessionStorage.setItem('pqrData', JSON.stringify(data));
        sessionStorage.setItem('clickInPolicy', '' + this.clickInPolicy);

        this.router.navigate(['/privacy-policy']);
    }
}
